import "./Clients.css";
import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaFilePdf,
  FaDownload,
  FaCalculator,
  FaTimes,
  FaPlay,
} from "react-icons/fa";

import { db } from "../../firebase/firebase";
import { collection, addDoc, Timestamp, onSnapshot, writeBatch, query, where } from "firebase/firestore";
import { getDocs, updateDoc, setDoc, doc } from "firebase/firestore";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { PROJECT_REF_BADGE_STYLE, assignProjectNumber } from "../../hooks/useClientsStatusMap";
import { useToast } from "./Invoicefiles/Usetoast";

import ToastContainer from "./Invoicefiles/Toastcontainer";
import ConfirmDialog from "./Invoicefiles/Confirmdialog";
import usePagination from "../../hooks/usePagination";
import Pagination from "./Pagination";

// ---- Company details used on generated PDFs. Update these to match your business. ----
const COMPANY = {
  name: "Sarrathi Constructions",
  address: "82, Jagannathan Salai, Anjugam Nagar,SRP Colony, Kolathur, Chennai - 600082.",
  phone: "98405 24873, 98407 98059",
  email: "build@sarrathiconstructions.com",
  website: "www.sarrathiconstructions.com",
};

// Logo lives in /public/logo, so it's referenced by URL (not imported) and
// fetched + converted to base64 at runtime, since jsPDF needs base64/data-URL image data.
const COMPANY_LOGO_URL = `${process.env.PUBLIC_URL}/logo/company logo.jpeg`;

// The four client-lifecycle tabs shown under the Topbar.
const CLIENT_TABS = ["In Talk", "In Progress", "Completed", "Rejected", "On Hold"];



// ---- Validation ----
// Allows multi-segment domains (e.g. company.co.in, mail.google.com) while still
// rejecting malformed addresses like "abc@gmail", "abc@", "abc@@gmail.com".
const EMAIL_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^[6-9]\d{9}$/;

// Client ID must always start with "CL-" (e.g. CL-001, CL-002, CL-100).
// It is now typed in manually by the admin instead of being generated —
// see the Client ID field at the top of the Add/Edit Client form below.
const CLIENT_ID_REGEX = /^CL-.+/i;

const emptyClient = {
  name: "",
  phone: "",
  email: "",
  address: "",
  projectType: "",
  status: "In Talk",
};

// Blank row shape for the "Move to Invoice" confirmation modal's Project
// Budget Breakdown. This becomes the client's Project Plan
// (budgetPlans/{clientDocId}) the moment Confirm Project is clicked — see
// confirmMoveToInvoice() below. Distinct from the Add/Edit modal's own
// budget rows (which use category/description/amount) because this one
// mirrors the estimation budget planner's Material / Qty / Unit Price
// shape, plus optional Supplier.
const emptyMoveBudgetRow = () => ({
  id: null, // assigned by caller
  name: "",
  quantity: "",
  unitPrice: "",
  supplier: "",
  notes: "",
});

function Clients() {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("In Talk");

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  const [logoBase64, setLogoBase64] = useState("");

  const [client, setClient] = useState(emptyClient);
  const [clients, setClients] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formErrors, setFormErrors] = useState({ email: "", phone: "" });

  // ---------------- Manual Client ID (Add Client) ----------------
  // The Client ID is no longer auto/randomly generated. The admin types it
  // in at the top of the Add Client form; it must start with "CL-" and be
  // unique across every existing client. While editing an existing client
  // this field is shown read-only (the ID never changes after creation).
  const [clientIdInput, setClientIdInput] = useState("CL-");
  const [clientIdError, setClientIdError] = useState("");

  // ---------------- Direct Project Confirmation (new client + "In Progress") ----------------
  // Only relevant on the Add Client form (never while editing — see
  // confirmProjectDirectly). Mirrors the Final Quotation / Amount Paid
  // fields on Estimation1.js's "Move to Invoice" modal, but collapses
  // Client -> Estimation -> Move to Invoice -> Invoice into one step.
  const [finalQuotation, setFinalQuotation] = useState("");
  const [advancePayment, setAdvancePayment] = useState("");
  const [projectFormErrors, setProjectFormErrors] = useState({
    finalQuotation: "",
    advancePayment: "",
  });
  const [confirmingProject, setConfirmingProject] = useState(false);

  // ---------------- Project Budget Breakdown (part of Direct Project Confirmation) ----------------
  // Only relevant alongside finalQuotation/advancePayment above — never
  // shown while editing. This becomes the project's ORIGINAL PLAN, saved
  // once at confirmation time and never overwritten by Estimation,
  // Invoice, or Materials (those pages will only ever read it).
  const [budgetRows, setBudgetRows] = useState([
    { id: 1, category: "", description: "", amount: "" },
  ]);
  const [budgetRowIdCounter, setBudgetRowIdCounter] = useState(2);
  const [budgetError, setBudgetError] = useState("");

  // ---------------- Estimations lookup (clientDocId -> { docId, amount }) ----------------
  const [estimationsMap, setEstimationsMap] = useState({});

  // When editing a client that already has an estimation, these hold the
  // editable amount + the estimation document's Firestore id so we UPDATE
  // the existing record instead of creating a duplicate.
  const [estimationEditAmount, setEstimationEditAmount] = useState("");
  const [estimationEditDocId, setEstimationEditDocId] = useState(null);
const { toasts, toast } = useToast();

// Replaces window.confirm() in deleteClient
const [pendingDeleteClient, setPendingDeleteClient] = useState(null);
const [deletingClient, setDeletingClient] = useState(false);
const [movingToRejected, setMovingToRejected] = useState(false);


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      // Numeric only, capped at 10 digits as the user types.
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setClient((prev) => ({ ...prev, phone: digitsOnly }));
      return;
    }

    // Switching status away from "In Progress" hides the Direct Project
    // Confirmation fields again — clear them + any errors so nothing
    // stale gets submitted if the user flips back and forth.
    if (name === "status" && value !== "In Progress") {
      setFinalQuotation("");
      setAdvancePayment("");
      setProjectFormErrors({ finalQuotation: "", advancePayment: "" });
      setBudgetRows([{ id: 1, category: "", description: "", amount: "" }]);
      setBudgetRowIdCounter(2);
      setBudgetError("");
    }

    setClient((prev) => ({ ...prev, [name]: value }));
  };

  // ---- Client ID: manual entry validation ----
  // Not empty, must start with "CL-", and must be unique across every
  // existing client (case-insensitive). `excludeDocId` lets a future caller
  // skip the client's own record when checking (not needed today since the
  // ID is never edited after creation, but kept for safety).
  const isDuplicateClientId = (id, excludeDocId = null) => {
    const normalized = id.trim().toLowerCase();
    return clients.some(
      (c) => c.id !== excludeDocId && (c.clientId || "").trim().toLowerCase() === normalized,
    );
  };

  const validateClientIdInput = () => {
    const value = clientIdInput.trim();

    if (!value) {
      setClientIdError("Client ID is required.");
      return false;
    }

    if (!CLIENT_ID_REGEX.test(value)) {
      setClientIdError('Client ID must start with "CL-" (e.g. CL-001).');
      return false;
    }

    if (isDuplicateClientId(value)) {
      setClientIdError("This Client ID already exists. Please use a different one.");
      return false;
    }

    setClientIdError("");
    return true;
  };

  const handleClientIdChange = (e) => {
    if (editingId) return; // read-only while editing — the ID never changes
    setClientIdInput(e.target.value);
    if (clientIdError) setClientIdError("");
  };

  const validateClientForm = () => {
    const errors = { email: "", phone: "" };
    let isValid = true;

    if (!EMAIL_REGEX.test(client.email.trim())) {
      errors.email = "Enter a valid email address (e.g. name@example.com).";
      isValid = false;
    }

    if (!PHONE_REGEX.test(client.phone.trim())) {
      errors.phone = "Enter a valid 10-digit Indian mobile number.";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Validates Final Quotation / Advance Payment for the Direct Project
  // Confirmation flow — same shape/rules as Estimation1.js's Move to
  // Invoice modal (finalQuotation > 0, 0 <= advance <= final).
  const validateProjectConfirmForm = () => {
    const errors = { finalQuotation: "", advancePayment: "" };
    let isValid = true;

    const final = Number(finalQuotation);
    const paid = advancePayment.trim() === "" ? 0 : Number(advancePayment);

    if (!finalQuotation.trim() || Number.isNaN(final) || final <= 0) {
      errors.finalQuotation = "Enter a valid final quotation amount.";
      isValid = false;
    }

    // Advance payment is optional — an empty field means no advance was
    // paid (Amount Paid = ₹0). Only a genuinely invalid (non-numeric or
    // negative) entry is rejected.
    if (advancePayment.trim() !== "" && (Number.isNaN(paid) || paid < 0)) {
      errors.advancePayment = "Enter a valid advance payment amount.";
      isValid = false;
    }

    if (isValid && paid > final) {
      errors.advancePayment = "Advance payment cannot exceed the final quotation.";
      isValid = false;
    }

    setProjectFormErrors(errors);

    // ---- Project Budget Breakdown validation ----
    // At least one real row (category + a positive amount), and the rows
    // must add up to exactly the final quotation — this is what makes the
    // breakdown a true original plan rather than a partial guess.
    const validRows = budgetRows.filter(
      (row) => row.category.trim() && Number(row.amount) > 0,
    );

    let budgetValid = true;

    if (validRows.length === 0) {
      setBudgetError("Add at least one budget breakdown row.");
      budgetValid = false;
    } else if (isValid) {
      const totalPlanned = validRows.reduce(
        (sum, row) => sum + Number(row.amount),
        0,
      );

      // Small epsilon to tolerate floating point rounding, not to permit
      // an actual mismatch.
      if (Math.abs(totalPlanned - final) > 0.01) {
        setBudgetError("Budget allocation must equal Final Quotation.");
        budgetValid = false;
      } else {
        setBudgetError("");
      }
    } else {
      setBudgetError("");
    }

    return isValid && budgetValid;
  };

  // Add/remove/update handlers for the Project Budget Breakdown rows.
  const addBudgetRow = () => {
    setBudgetRows((prev) => [
      ...prev,
      { id: budgetRowIdCounter, category: "", description: "", amount: "" },
    ]);
    setBudgetRowIdCounter((prev) => prev + 1);
  };

  const removeBudgetRow = (id) => {
    setBudgetRows((prev) => (prev.length === 1 ? prev : prev.filter((row) => row.id !== id)));
  };

  const updateBudgetRow = (id, field, value) => {
    if (field === "amount" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setBudgetRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );

    if (budgetError) setBudgetError("");
  };

  const handleFinalQuotationChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFinalQuotation(value);
      if (projectFormErrors.finalQuotation) {
        setProjectFormErrors((prev) => ({ ...prev, finalQuotation: "" }));
      }
    }
  };

  const handleAdvancePaymentChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setAdvancePayment(value);
      if (projectFormErrors.advancePayment) {
        setProjectFormErrors((prev) => ({ ...prev, advancePayment: "" }));
      }
    }
  };

  // Live total across all budget breakdown rows (including partially-typed
  // ones), so the running total updates as the user types.
  const totalPlannedAmount = useMemo(
    () => budgetRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [budgetRows],
  );

  // ---------------- Estimation modal (create new estimation from In Talk tab) ----------------
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  const [estimationClient, setEstimationClient] = useState(null);
  const [estimationAmount, setEstimationAmount] = useState("");
  const [savingEstimation, setSavingEstimation] = useState(false);

  // ---------------- Move to Invoice (from the Estimation modal) ----------------
  // Triggered by the "Move to Invoice" button inside the Estimation modal
  // above, for an existing "In Talk" client who does NOT have an
  // estimation yet. Opens a dedicated confirmation modal (Client Name,
  // Client ID, Final Estimation, live Advance Payment) plus a Project
  // Budget Breakdown table — same idea as Direct Project Confirmation's
  // budget rows above, but shaped like the estimation budget planner
  // (Material / Qty / Unit Price / Total / Supplier / Notes) per the
  // "Move to Invoice" workflow spec. Confirming writes the client status,
  // estimation (invoiced: true), invoice, and budgetPlans/{clientDocId}
  // "Project Plan" all in a single Firestore batch — see
  // confirmMoveToInvoice() below. Existing payment allocation, Materials'
  // rendering, and Estimation1.js's own Move-to-Invoice flow are
  // untouched; this only ever creates data those pages already know how
  // to read.
  const [showMoveToInvoiceModal, setShowMoveToInvoiceModal] = useState(false);
  const [moveToInvoiceClient, setMoveToInvoiceClient] = useState(null);
  const [moveFinalEstimation, setMoveFinalEstimation] = useState(0);
  const [moveFinalEstimationInput, setMoveFinalEstimationInput] = useState("");
  const [moveFinalEstimationError, setMoveFinalEstimationError] = useState("");
  const [moveAdvancePayment, setMoveAdvancePayment] = useState("");
  const [moveAdvanceError, setMoveAdvanceError] = useState("");
  const [moveBudgetRows, setMoveBudgetRows] = useState([
    { ...emptyMoveBudgetRow(), id: 1 },
  ]);
  const [moveBudgetRowIdCounter, setMoveBudgetRowIdCounter] = useState(2);
  const [moveBudgetError, setMoveBudgetError] = useState("");
 
  // Tracks whether the admin has manually typed into Final Quotation yet.
  // While false, Final Quotation auto-fills from the materials total
  // (Estimation Grand Total) as rows change. Once true, it stops
  // auto-syncing so a manual override is never silently overwritten.
  const [finalQuotationTouched, setFinalQuotationTouched] = useState(false);

  // "Is this the final quotation?" Yes/No confirmation — must be explicitly
  // answered "Yes" before Continue & Move to Invoice is allowed.
  const [moveQuotationConfirmed, setMoveQuotationConfirmed] = useState(null); // null | true | false

  
  const [movingToInvoice, setMovingToInvoice] = useState(false);

  // ---------------- Realtime clients list ----------------
  // Using onSnapshot (instead of a one-time getDocs) means this page is the
  // single source of truth for status AND stays live: if Estimation,
  // Invoice, or Materials updates a client's status, this table updates
  // itself immediately with no manual refresh. This also means a client
  // created via Direct Project Confirmation appears in the table the
  // instant its addDoc resolves, with no extra wiring needed here.
  useEffect(() => {
  const unsubscribe = onSnapshot(
    collection(db, "clients"),
    (snapshot) => {
      const list = [];
      snapshot.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });

      // Newest client first — sorted by the actual creation timestamp,
      // not Client ID or name, so ordering is correct regardless of how
      // clients were added.
      list.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      });

      setClients(list);
    },
    (err) => console.error("Failed to subscribe to clients:", err),
  );

  return () => unsubscribe();
}, []);

  const editClient = (clientData) => {
    setClient({
      name: clientData.name || "",
      phone: clientData.phone || "",
      email: clientData.email || "",
      address: clientData.address || "",
      projectType: clientData.projectType || "",
      status: clientData.status || "In Talk",
    });

    setEditingId(clientData.id);
    setFormErrors({ email: "", phone: "" });

    // Client ID is permanent once created — shown read-only while editing.
    setClientIdInput(clientData.clientId || "");
    setClientIdError("");

    // Direct Project Confirmation fields are Add-Client-only — always
    // reset them when opening Edit, regardless of the client's status.
    setFinalQuotation("");
    setAdvancePayment("");
    setProjectFormErrors({ finalQuotation: "", advancePayment: "" });
    setBudgetRows([{ id: 1, category: "", description: "", amount: "" }]);
    setBudgetRowIdCounter(2);
    setBudgetError("");

    // If this client already has an estimation, load it so it can be edited
    // alongside the client details. If not, leave it blank/hidden.
    const existingEstimation = estimationsMap[clientData.id];
    if (existingEstimation) {
      setEstimationEditDocId(existingEstimation.docId);
      setEstimationEditAmount(String(existingEstimation.amount ?? ""));
    } else {
      setEstimationEditDocId(null);
      setEstimationEditAmount("");
    }

    setShowModal(true);
  };

  const viewClient = (clientData) => {
    setViewingClient(clientData);
    setShowViewModal(true);
  };

 const deleteClient = (clientData) => {
  setPendingDeleteClient(clientData);
};

// NEW — only ever reachable from the "In Talk" choice modal. Reuses the
// exact same status-update pattern used throughout the app (see
// useClientsStatusMap.js docblock): a plain updateDoc on the client's
// status field. No new document, no new collection — the realtime
// onSnapshot subscription already wired up above picks this up instantly
// and the client re-renders into the Rejected tab on its own.
const confirmMoveToRejected = async () => {
  if (!pendingDeleteClient) return;
  const id = pendingDeleteClient.id;

  setMovingToRejected(true);
  try {
    await updateDoc(doc(db, "clients", id), { status: "Rejected" });
    toast.success("Client Moved to Rejected");
  } catch (err) {
    console.error(err);
    toast.error("Failed to move client to Rejected.");
  } finally {
    setMovingToRejected(false);
    setPendingDeleteClient(null);
  }
};

// Resume from On Hold — same status-update pattern as
// confirmMoveToRejected above. The linked invoice document is never
// touched, so Invoice.js picks the project back up with its full
// existing payment history and no duplicate is ever created.
const [resumingClientId, setResumingClientId] = useState(null);

// Local equivalent of the shared getProjectRef helper — this page keeps
// its own realtime `clients` array rather than the clientsById map used
// elsewhere, so the same "rank by creation order among same-clientId
// docs" logic is reproduced here against that array. Returns null for a
// single-project client so the badge is only shown when it's actually
// needed.
const getProjectRef = (clientDocId) => {
  const target = clients.find((c) => c.id === clientDocId);
  if (!target || !target.clientId) return null;

  const targetKey = target.clientId.trim().toLowerCase();
  const siblings = clients.filter(
    (c) => (c.clientId || "").trim().toLowerCase() === targetKey,
  );
  if (siblings.length <= 1) return null;

  const sorted = [...siblings].sort((a, b) => {
    const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return aTime - bTime;
  });

  const idx = sorted.findIndex((c) => c.id === clientDocId);
  return idx === -1 ? null : `P-${String(idx + 1).padStart(2, "0")}`;
};

const renderProjectRefBadge = (clientDocId) => {
  const ref = getProjectRef(clientDocId);
  return ref ? <span style={PROJECT_REF_BADGE_STYLE}>{ref}</span> : null;
};

const resumeFromOnHold = async (clientData) => {
  setResumingClientId(clientData.id);
  try {
    await updateDoc(doc(db, "clients", clientData.id), { status: "In Progress" });
    toast.success("Project Resumed");
  } catch (err) {
    console.error(err);
    toast.error("Failed to resume project.");
  } finally {
    setResumingClientId(null);
  }
};

// ---------------------------------------------------------------------
// Add Project — lets an EXISTING client (regardless of current status:
// In Talk / In Progress / Completed / On Hold / Rejected) start a NEW,
// separate project without creating a duplicate client record.
//
// Implementation: the new project is its own `clients` document (so it
// gets its own Firestore doc id === its own clientDocId — the exact
// identifier Estimation/Invoice/Payments/Materials already key every
// piece of project data off of), but it reuses the SAME `clientId` and
// `name` as the client it was launched from, so it never appears as a
// different client — only as another project under the same Client ID.
// `sourceClientDocId` is kept purely for reference; no other page reads
// it. The new project always starts at the app's existing default
// initial status, "In Talk", and flows through the completely
// unmodified Estimation -> Move to Invoice -> Payments -> Materials
// pipeline from there — exactly like any other In Talk client.
// ---------------------------------------------------------------------
const [showAddProjectModal, setShowAddProjectModal] = useState(false);
const [addProjectSource, setAddProjectSource] = useState(null);
const [addProjectForm, setAddProjectForm] = useState({
  phone: "",
  email: "",
  address: "",
  projectType: "",
});
const [addProjectErrors, setAddProjectErrors] = useState({
  email: "",
  phone: "",
  projectType: "",
});
const [addProjectSaving, setAddProjectSaving] = useState(false);

const openAddProject = (clientData) => {
  setAddProjectSource(clientData);
  // Pre-fill from the client's existing details as a starting point —
  // phone/email/address/projectType are all editable per-project since a
  // new project may have a different site address/project type/contact.
  setAddProjectForm({
    phone: clientData.phone || "",
    email: clientData.email || "",
    address: clientData.address || "",
    projectType: "",
  });
  setAddProjectErrors({ email: "", phone: "", projectType: "" });
  setShowAddProjectModal(true);
};

const closeAddProject = () => {
  if (addProjectSaving) return;
  setShowAddProjectModal(false);
  setAddProjectSource(null);
  setAddProjectForm({ phone: "", email: "", address: "", projectType: "" });
  setAddProjectErrors({ email: "", phone: "", projectType: "" });
};

const handleAddProjectChange = (e) => {
  const { name, value } = e.target;

  if (name === "phone") {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
    setAddProjectForm((prev) => ({ ...prev, phone: digitsOnly }));
    return;
  }

  setAddProjectForm((prev) => ({ ...prev, [name]: value }));
  if (addProjectErrors[name]) {
    setAddProjectErrors((prev) => ({ ...prev, [name]: "" }));
  }
};

// Reuses the SAME validation rules as the main Add/Edit Client form —
// email/phone regexes are shared constants, not weakened here.
const validateAddProjectForm = () => {
  const errors = { email: "", phone: "", projectType: "" };
  let isValid = true;

  if (!EMAIL_REGEX.test(addProjectForm.email.trim())) {
    errors.email = "Enter a valid email address (e.g. name@example.com).";
    isValid = false;
  }

  if (!PHONE_REGEX.test(addProjectForm.phone.trim())) {
    errors.phone = "Enter a valid 10-digit Indian mobile number.";
    isValid = false;
  }

  if (!addProjectForm.projectType) {
    errors.projectType = "Select a project type for this project.";
    isValid = false;
  }

  setAddProjectErrors(errors);
  return isValid;
};

const saveAddProject = async () => {
  if (!addProjectSource) return;
  if (!validateAddProjectForm()) return;

  setAddProjectSaving(true);
try {
  const projectNumber = await assignProjectNumber(addProjectSource.clientId);
  await addDoc(collection(db, "clients"), {
    clientId: addProjectSource.clientId,
    name: addProjectSource.name,
    phone: addProjectForm.phone,
    email: addProjectForm.email,
    address: addProjectForm.address,
    projectType: addProjectForm.projectType,
    status: "In Talk",
    sourceClientDocId: addProjectSource.id,
    projectNumber,
    createdAt: Timestamp.now(),
  });
    toast.success("New Project Added Successfully");
    closeAddProject();
  } catch (err) {
    console.error(err);
    toast.error("Failed to add new project.");
  } finally {
    setAddProjectSaving(false);
  }
};

const confirmDeleteClient = async () => {
  if (!pendingDeleteClient) return;
  const id = pendingDeleteClient.id;

  setDeletingClient(true);
  try {
    const batch = writeBatch(db);

    // The client record itself.
    batch.delete(doc(db, "clients", id));

    // Estimation(s) linked via clientDocId. Queried directly (not read
    // from the cached estimationsMap) so nothing is missed even if that
    // map hasn't refreshed since the estimation was created.
    const estimationsSnap = await getDocs(
      query(collection(db, "estimations"), where("clientDocId", "==", id)),
    );
    estimationsSnap.forEach((docSnap) => batch.delete(docSnap.ref));

    // Invoice(s) linked via clientDocId — this was the actual root cause
    // of the bug: leaving these behind meant Invoice.js/Payments.js,
    // unable to find the (now-deleted) client doc, fell back to treating
    // the orphaned invoice as status "In Progress" and showed it again.
    // Each invoice's own paymentAllocations doc (id === invoice id, the
    // Daily Material Logs / Construction Expense data) is removed too.
    const invoicesSnap = await getDocs(
      query(collection(db, "invoices"), where("clientDocId", "==", id)),
    );
    invoicesSnap.forEach((docSnap) => {
      batch.delete(docSnap.ref);
      batch.delete(doc(db, "paymentAllocations", docSnap.id));
    });

    // Budget plan — doc id === clientDocId, one per client.
    batch.delete(doc(db, "budgetPlans", id));

    await batch.commit();

    toast.success("Client Deleted Successfully");
    await fetchEstimationsMap();
  } catch (err) {
    console.error(err);
    toast.error("Failed to delete client.");
  } finally {
    setDeletingClient(false);
    setPendingDeleteClient(null);
  }
};

  const fetchEstimationsMap = async () => {
    try {
      const snapshot = await getDocs(collection(db, "estimations"));
      const map = {};

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.clientDocId) {
          map[data.clientDocId] = {
            docId: docSnap.id,
            amount: data.estimationAmount,
          };
        }
      });

      setEstimationsMap(map);
    } catch (err) {
      console.error("Failed to load estimations:", err);
    }
  };

  useEffect(() => {
    fetchEstimationsMap();
  }, []);

  // Load the company logo once and convert it to base64 so jsPDF can embed it.
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch(encodeURI(COMPANY_LOGO_URL));
        if (!response.ok) throw new Error("Logo not found");
        const blob = await response.blob();

        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.onerror = () => console.error("Failed to read company logo");
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error("Could not load company logo:", err);
      }
    };

    loadLogo();
  }, []);

  // Lock background scroll while the Move to Invoice modal is open
useEffect(() => {
  if (showMoveToInvoiceModal) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
  return () => {
    document.body.style.overflow = "";
  };
}, [showMoveToInvoiceModal]);

  const resetClientForm = () => {
    setClient(emptyClient);
    setEditingId(null);
    setFormErrors({ email: "", phone: "" });
    setClientIdInput("CL-");
    setClientIdError("");
    setEstimationEditAmount("");
    setEstimationEditDocId(null);
    setFinalQuotation("");
    setAdvancePayment("");
    setProjectFormErrors({ finalQuotation: "", advancePayment: "" });
    setBudgetRows([{ id: 1, category: "", description: "", amount: "" }]);
    setBudgetRowIdCounter(2);
    setBudgetError("");
  };

  // ---------------------------------------------------------------------
  // Direct Project Confirmation — "Confirm Project" on a brand-new client
  // whose status is "In Progress".
  //
  // Collapses the normal Client -> Estimation -> Move to Invoice -> Invoice
  // pipeline into a single action, reusing the exact same document shapes
  // Estimation1.js's finalizeMoveToInvoice() writes, so Estimation.js and
  // Invoice.js need no special-casing at all:
  //   - the estimation doc is created with `invoiced: true` up front, so
  //     isMovable() in Estimation1.js is false immediately (no dangling
  //     "Move to Invoice" button for this row)
  //   - the invoice doc is created with the advance payment already
  //     recorded as payment #1 (same `invoiceNumber: 1` shape used by
  //     finalizeMoveToInvoice), so balances/history render normally
  //   - the client doc is created once, status "In Progress" — the single
  //     source of truth every other page already reads from
  //
  // Nothing here duplicates a client record or writes status anywhere
  // except the `clients` collection. The Client ID used is the one the
  // admin manually typed into the Client ID field (validated by the
  // caller — see saveClient below — before this function ever runs).
  // ---------------------------------------------------------------------
  const confirmProjectDirectly = async () => {
    if (!validateProjectConfirmForm()) return;

    setConfirmingProject(true);

    const final = Number(finalQuotation);
    const paid = Number(advancePayment);
    const balance = final - paid;
    const clientId = clientIdInput.trim();
    const today = new Date().toISOString().slice(0, 10);
    const projectNumber = await assignProjectNumber(clientId);

    // The project's ORIGINAL PLAN — captured once here at confirmation
    // time. Nothing else in the app is allowed to overwrite this; later,
    // Estimation/Invoice/Materials will only read it.
    const budgetBreakdown = budgetRows
      .filter((row) => row.category.trim() && Number(row.amount) > 0)
      .map((row) => ({
        category: row.category.trim(),
        description: row.description.trim(),
        amount: Number(row.amount),
      }));

    const totalPlanned = budgetBreakdown.reduce((sum, row) => sum + row.amount, 0);

try {
  const clientDocRef = await addDoc(collection(db, "clients"), {
    clientId,
    name: client.name,
    phone: client.phone,
    email: client.email,
    address: client.address,
    projectType: client.projectType,
    status: "In Progress",
    budgetBreakdown,
    totalPlannedAmount: totalPlanned,
    projectNumber,
    createdAt: Timestamp.now(),
  });

      const estimationDocRef = await addDoc(collection(db, "estimations"), {
        clientDocId: clientDocRef.id,
        clientId,
        name: client.name,
        email: client.email,
        address: client.address,
        projectType: client.projectType,
        estimationAmount: final,
        // Pre-marked invoiced so this behaves exactly like a project that
        // already went through "Move to Invoice" the normal way.
        invoiced: true,
        createdAt: Timestamp.now(),
      });

      const initialPayments =
        paid > 0
          ? [
              {
                invoiceNumber: 1,
                paymentAmount: paid,
                paymentDate: today,
                balanceAfterPayment: balance,
                createdAt: new Date().toISOString(),
              },
            ]
          : [];

      await addDoc(collection(db, "invoices"), {
        estimationId: estimationDocRef.id,
        clientDocId: clientDocRef.id,
        clientId,
        clientName: client.name,
        email: client.email,
        address: client.address,
        projectType: client.projectType,
        originalEstimation: final,
        finalEstimation: final,
        amountPaid: paid,
        balanceAmount: balance,
        payments: initialPayments,
        paymentCount: initialPayments.length,
        latestPaymentDate: initialPayments.length ? today : null,
        createdAt: Timestamp.now(),
      });

      // Seed budgetPlans/{clientDocId} — the exact same document shape
      // Materials.js and Estimation's Budget Planning modal already read
      // from/write to (see Materials.js: fetchBudgetPlan, and the
      // budgetPlans-driven project picker). This lets Materials surface
      // the original plan immediately, without any change to Materials.js
      // or Estimation.js. This document is only ever CREATED here; it is
      // not touched again by this page.
      await setDoc(doc(db, "budgetPlans", clientDocRef.id), {
        clientDocId: clientDocRef.id,
        quotationAmount: final,
        items: budgetBreakdown.map((row) => ({
          name: row.description ? `${row.category} - ${row.description}` : row.category,
          pricePerQty: row.amount,
          quantity: 1,
          budget: row.amount,
          notes: row.category,
        })),
        totalPlanned,
        createdAt: Timestamp.now(),
      });

      toast.success("Project Confirmed Successfully");

      resetClientForm();
      setShowModal(false);

      await fetchEstimationsMap();
    } catch (err) {
      console.error(err);
      toast.error("Failed to confirm project.");
    } finally {
      setConfirmingProject(false);
    }
  };

  const saveClient = async () => {
    if (!validateClientForm()) {
      return;
    }

    // A new client always needs a valid, unique, manually entered Client ID
    // before anything is written to Firestore. Existing clients keep their
    // original ID forever, so this only applies when creating.
    if (!editingId && !validateClientIdInput()) {
      return;
    }

    // If we're editing a client that has an estimation attached, validate
    // that amount too before writing anything to Firestore.
    if (editingId && estimationEditDocId) {
      if (!estimationEditAmount || Number(estimationEditAmount) <= 0) {
        toast.error("Please enter a valid estimation amount.");
        return;
      }
    }

    // Direct Project Confirmation — only for brand-new clients being set
    // straight to "In Progress". Editing an existing client's status still
    // goes through the normal updateDoc branch below, unchanged.
    if (!editingId && client.status === "In Progress") {
      await confirmProjectDirectly();
      return;
    }

    try {
      if (editingId) {
        await updateDoc(doc(db, "clients", editingId), {
          ...client,
        });

        // Keep the linked estimation document (if any) in sync — update it
        // in place rather than creating a new record.
        if (estimationEditDocId) {
          await updateDoc(doc(db, "estimations", estimationEditDocId), {
            name: client.name,
            email: client.email,
            address: client.address,
            projectType: client.projectType,
            estimationAmount: Number(estimationEditAmount),
          });

          setEstimationsMap((prev) => ({
            ...prev,
            [editingId]: {
              docId: estimationEditDocId,
              amount: Number(estimationEditAmount),
            },
          }));
        }

        toast.success("Client Updated Successfully");
      } else {
  const projectNumber = await assignProjectNumber(clientIdInput.trim());
  await addDoc(collection(db, "clients"), {
    clientId: clientIdInput.trim(),
    ...client,
    status: "In Talk",
    projectNumber,
    createdAt: Timestamp.now(),
  });
  toast.success("Client Added Successfully");
}

      resetClientForm();

      setShowModal(false);
    } catch (err) {
      console.error(err);

      toast.error("Something went wrong.");
    }
  };

  // ---------------- Estimation modal handlers ----------------
  const openEstimationModal = (clientData) => {
    setEstimationClient(clientData);
    setEstimationAmount("");
    setShowEstimationModal(true);
  };

  const closeEstimationModal = () => {
    setShowEstimationModal(false);
    setEstimationClient(null);
    setEstimationAmount("");
  };

  // ---------------------------------------------------------------------
  // Move to Estimation — no estimation amount is required anymore. The
  // Estimation/Quotation amount is now always derived from Budget
  // Planning on the Estimation page (see Estimation1.js's
  // saveBudgetPlan), so this simply creates the estimation record —
  // starting at ₹0 until a budget plan is built — and links it to the
  // client. The optional amount field below is kept purely as a
  // convenience for the admin; leaving it blank is completely fine.
  // ---------------------------------------------------------------------
  const moveToEstimation = async () => {
    if (!estimationClient) return;

    if (estimationsMap[estimationClient.id]) {
      toast.warning("An estimation already exists for this client.");
      closeEstimationModal();
      return;
    }

    setSavingEstimation(true);

    try {
      const initialAmount = Number(estimationAmount) || 0;

      const docRef = await addDoc(collection(db, "estimations"), {
        clientDocId: estimationClient.id,
        clientId: estimationClient.clientId || "",
        name: estimationClient.name || "",
        email: estimationClient.email || "",
        address: estimationClient.address || "",
        projectType: estimationClient.projectType || "",
        estimationAmount: initialAmount,
        invoiced: false,
        createdAt: Timestamp.now(),
      });

      setEstimationsMap((prev) => ({
        ...prev,
        [estimationClient.id]: {
          docId: docRef.id,
          amount: initialAmount,
        },
      }));

      toast.success("Moved to Estimation Successfully");

      closeEstimationModal();
    } catch (err) {
      console.error(err);
      toast.error("Failed to move client to estimation.");
    } finally {
      setSavingEstimation(false);
    }
  };

  // ---------------------------------------------------------------------
  // Move to Invoice — Step 1: simply hands off to the dedicated
  // confirmation modal (Client Name / Client ID / Final Estimation /
  // Advance Payment + Project Budget Breakdown). The Final Estimation
  // amount is now entered inside that confirmation modal itself — this
  // click no longer needs anything typed in beforehand. Nothing is
  // written to Firestore yet — that only happens in confirmMoveToInvoice()
  // once the breakdown exactly matches the Final Estimation.
  // ---------------------------------------------------------------------
  const moveToInvoice = () => {
    if (!estimationClient) return;

    if (estimationsMap[estimationClient.id]) {
      toast.warning("An estimation already exists for this client.");
      closeEstimationModal();
      return;
    }

    setMoveToInvoiceClient(estimationClient);
    setMoveFinalEstimation(0);
    setMoveFinalEstimationInput("");
    setMoveFinalEstimationError("");
    setMoveAdvancePayment("");
    setMoveAdvanceError("");
    setMoveBudgetRows([{ ...emptyMoveBudgetRow(), id: 1 }]);
    setMoveBudgetRowIdCounter(2);
    setMoveBudgetError("");
    setFinalQuotationTouched(false);
    setMoveQuotationConfirmed(null);

    closeEstimationModal();
    setShowMoveToInvoiceModal(true);
  };

  const closeMoveToInvoiceModal = () => {
    if (movingToInvoice) return;
    setShowMoveToInvoiceModal(false);
    setMoveToInvoiceClient(null);
    setMoveFinalEstimation(0);
    setMoveFinalEstimationInput("");
    setMoveFinalEstimationError("");
    setMoveAdvancePayment("");
    setMoveAdvanceError("");
    setMoveBudgetRows([{ ...emptyMoveBudgetRow(), id: 1 }]);
    setMoveBudgetRowIdCounter(2);
    setMoveBudgetError("");
    setFinalQuotationTouched(false);
    setMoveQuotationConfirmed(null);
  };

  // Final Estimation is now typed in here, inside the confirmation modal,
  // instead of the earlier Estimation popup. `moveFinalEstimation` (the
  // numeric value everything else below reads from) is kept in sync as
  // the admin types.
  const handleMoveFinalEstimationChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFinalQuotationTouched(true);
      setMoveFinalEstimationInput(value);
      setMoveFinalEstimation(Number(value) || 0);
      if (moveFinalEstimationError) setMoveFinalEstimationError("");
    }
  };

  const handleMoveAdvanceChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setMoveAdvancePayment(value);
      if (moveAdvanceError) setMoveAdvanceError("");
    }
  };

  const addMoveBudgetRow = () => {
    setMoveBudgetRows((prev) => [
      ...prev,
      { ...emptyMoveBudgetRow(), id: moveBudgetRowIdCounter },
    ]);
    setMoveBudgetRowIdCounter((prev) => prev + 1);
  };

  const removeMoveBudgetRow = (id) => {
    setMoveBudgetRows((prev) =>
      prev.length === 1 ? prev : prev.filter((row) => row.id !== id),
    );
  };

  const updateMoveBudgetRow = (id, field, value) => {
    if (
      (field === "quantity" || field === "unitPrice") &&
      value !== "" &&
      !/^\d*\.?\d*$/.test(value)
    ) {
      return;
    }

    setMoveBudgetRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );

    if (moveBudgetError) setMoveBudgetError("");
  };

  // Live running total across every row (Qty x Unit Price), including
  // partially-typed ones, so the "Total Planned / Remaining" chip updates
  // as the user types — matches the estimation budget planner's behaviour.
  const moveTotalPlannedAmount = useMemo(
    () =>
      moveBudgetRows.reduce(
        (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.unitPrice) || 0),
        0,
      ),
    [moveBudgetRows],
  );

  // Keep Final Quotation synced to the Estimation Grand Total (materials
  // total) as long as the admin hasn't manually typed a different amount.
  // The moment they touch the Final Quotation field, this stops — see
  // handleMoveFinalEstimationChange below, which sets finalQuotationTouched.
  useEffect(() => {
    if (finalQuotationTouched) return;
    const total = moveTotalPlannedAmount;
    setMoveFinalEstimationInput(total > 0 ? String(total) : "");
    setMoveFinalEstimation(total);
  }, [moveTotalPlannedAmount, finalQuotationTouched]);

  // ---------------------------------------------------------------------
  // Move to Invoice — Step 2/3: validate, then commit Client status,
  // Estimation (invoiced: true), Invoice, and the budgetPlans/{clientDocId}
  // "Project Plan" together in a single Firestore batch so the operation
  // is atomic — either everything lands or nothing does.
  //
  // This never touches paymentAllocations (existing Payment Allocation
  // system is untouched) and never duplicates the client record — it
  // updates the SAME clients/{id} doc every other page already reads.
  // ---------------------------------------------------------------------
  const confirmMoveToInvoice = async () => {
    if (!moveToInvoiceClient) return;

    const final = moveFinalEstimation;
    const paid = moveAdvancePayment.trim() === "" ? 0 : Number(moveAdvancePayment);

    let valid = true;

    if (!moveFinalEstimationInput.trim() || Number.isNaN(final) || final <= 0) {
      setMoveFinalEstimationError("Enter a valid Final Estimation amount.");
      valid = false;
    } else {
      setMoveFinalEstimationError("");
    }

    // Advance payment is optional — an empty field means no advance was
    // paid (Amount Paid = ₹0, Balance = full Final Estimation). Only a
    // genuinely invalid (non-numeric or negative) entry is rejected.
    if (moveAdvancePayment.trim() !== "" && (Number.isNaN(paid) || paid < 0)) {
      setMoveAdvanceError("Enter a valid advance payment amount.");
      valid = false;
    } else if (paid > final) {
      setMoveAdvanceError("Advance payment cannot exceed the Final Estimation.");
      valid = false;
    } else {
      setMoveAdvanceError("");
    }

    const validRows = moveBudgetRows.filter(
      (row) => row.name.trim() && Number(row.quantity) > 0 && Number(row.unitPrice) > 0,
    );

    if (validRows.length === 0) {
      setMoveBudgetError("Add at least one budget breakdown row.");
      toast.error("Add at least one budget breakdown row.");
      valid = false;
    } else {
      setMoveBudgetError("");
    }

    if (moveQuotationConfirmed !== true) {
      toast.error('Please confirm "Is this the final quotation?" before continuing.');
      valid = false;
    }

    if (!valid) return;

    setMovingToInvoice(true);

    const clientDocId = moveToInvoiceClient.id;
    const balance = final - paid;
    const today = new Date().toISOString().slice(0, 10);

    const budgetBreakdown = validRows.map((row) => ({
      name: row.name.trim(),
      quantity: Number(row.quantity),
      pricePerQty: Number(row.unitPrice),
      budget: Number(row.quantity) * Number(row.unitPrice),
      supplier: row.supplier.trim(),
      notes: row.notes.trim(),
    }));

    const totalPlanned = budgetBreakdown.reduce((sum, row) => sum + row.budget, 0);

    try {
      const batch = writeBatch(db);

      // 1. Client — single source of truth for status.
      batch.update(doc(db, "clients", clientDocId), {
        status: "In Progress",
      });

      // 2. Estimation — created here (this flow only ever runs for a
      // client with no estimation yet), pre-marked invoiced so it behaves
      // exactly like a project that went through Estimation1.js's own
      // Move to Invoice, and so its "Move to Invoice" button there is
      // already hidden the moment this lands.
      const estimationRef = doc(collection(db, "estimations"));
      batch.set(estimationRef, {
        clientDocId,
        clientId: moveToInvoiceClient.clientId || "",
        name: moveToInvoiceClient.name || "",
        email: moveToInvoiceClient.email || "",
        address: moveToInvoiceClient.address || "",
        projectType: moveToInvoiceClient.projectType || "",
        estimationAmount: totalPlanned,
        invoiced: true,
        createdAt: Timestamp.now(),
      });

      // 3. Invoice — same shape finalizeMoveToInvoice()/confirmProjectDirectly()
      // already write, so Invoice.js needs no special-casing.
      const initialPayments =
        paid > 0
          ? [
              {
                invoiceNumber: 1,
                paymentAmount: paid,
                paymentDate: today,
                balanceAfterPayment: balance,
                createdAt: new Date().toISOString(),
              },
            ]
          : [];

      const invoiceRef = doc(collection(db, "invoices"));
      batch.set(invoiceRef, {
        estimationId: estimationRef.id,
        clientDocId,
        clientId: moveToInvoiceClient.clientId || "",
        clientName: moveToInvoiceClient.name || "",
        email: moveToInvoiceClient.email || "",
        address: moveToInvoiceClient.address || "",
        projectType: moveToInvoiceClient.projectType || "",
        originalEstimation: final,
        finalEstimation: final,
        amountPaid: paid,
        balanceAmount: balance,
        payments: initialPayments,
        paymentCount: initialPayments.length,
        latestPaymentDate: initialPayments.length ? today : null,
        createdAt: Timestamp.now(),
      });

      // 4. Project Plan — budgetPlans/{clientDocId}, the exact document
      // Estimation1.js's Budget Planning and Materials.js already read
      // from/write to. This is the master planned budget; payment
      // allocation rounds (paymentAllocations) remain completely separate
      // and are never touched here.
      batch.set(doc(db, "budgetPlans", clientDocId), {
        clientDocId,
        quotationAmount: totalPlanned,
        items: budgetBreakdown,
        totalPlanned,
        createdAt: Timestamp.now(),
      });

      await batch.commit();

      toast.success("Project Confirmed — Moved to Invoice Successfully");

      setShowMoveToInvoiceModal(false);
      setMoveToInvoiceClient(null);
      setMoveFinalEstimation(0);
      setMoveAdvancePayment("");
      setMoveAdvanceError("");
      setMoveBudgetRows([{ ...emptyMoveBudgetRow(), id: 1 }]);
      setMoveBudgetRowIdCounter(2);
      setMoveBudgetError("");

      await fetchEstimationsMap();
    } catch (err) {
      console.error(err);
      toast.error("Failed to move project to invoice.");
    } finally {
      setMovingToInvoice(false);
    }
  };

  // ---------------- Search (real-time, case-insensitive) + tab filter ----------------
  const tabFilteredClients = useMemo(() => {
    return clients.filter((c) => (c.status || "In Talk") === activeTab);
  }, [clients, activeTab]);

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tabFilteredClients;

    return tabFilteredClients.filter((c) => {
      const clientId = (c.clientId || "").toLowerCase();
      const name = (c.name || "").toLowerCase();
      const phone = (c.phone || "").toLowerCase();
      const email = (c.email || "").toLowerCase();

      return (
        clientId.includes(term) ||
        name.includes(term) ||
        phone.includes(term) ||
        email.includes(term)
      );
    });
  }, [search, tabFilteredClients]);

  // Pagination — operates on the already sorted/filtered list. The
  // resetKey combines the active tab and search text, so switching tabs
  // or typing a new search always snaps back to page 1.
  const {
    pageData: paginatedClients,
    ...clientsPagination
  } = usePagination(filteredClients, { resetKey: `${activeTab}|${search}` });

  // ---------------- Helpers ----------------
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const formatRupees = (value) => {
    const num = Number(value);
    if (value === undefined || value === null || Number.isNaN(num)) return "-";
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  const addPdfHeader = (pdfDoc) => {
    const pageWidth = pdfDoc.internal.pageSize.getWidth();

    if (logoBase64) {
      try {
        pdfDoc.addImage(logoBase64, "JPEG", 40, 30, 50, 50);
      } catch {
        // ignore bad image data
      }
    }

    const textX = logoBase64 ? 100 : 40;

    pdfDoc.setFont("helvetica", "bold");
    pdfDoc.setFontSize(16);
    pdfDoc.setTextColor(20, 30, 50);
    pdfDoc.text(COMPANY.name, textX, 48);

    pdfDoc.setFont("helvetica", "normal");
    pdfDoc.setFontSize(9);
    pdfDoc.setTextColor(90, 90, 90);
    pdfDoc.text(COMPANY.address, textX, 62);
    pdfDoc.text(`Phone: ${COMPANY.phone}`, textX, 74);
    pdfDoc.text(`Email: ${COMPANY.email}   Website: ${COMPANY.website}`, textX, 86);

    pdfDoc.setDrawColor(200, 200, 200);
    pdfDoc.line(40, 96, pageWidth - 40, 96);
  };

  const addPdfFooter = (pdfDoc) => {
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const pageHeight = pdfDoc.internal.pageSize.getHeight();

    const now = new Date();
    const dateStr = now.toLocaleDateString("en-IN");
    const timeStr = now.toLocaleTimeString("en-IN");

    pdfDoc.setDrawColor(200, 200, 200);
    pdfDoc.line(40, pageHeight - 70, pageWidth - 40, pageHeight - 70);

    pdfDoc.setFont("helvetica", "normal");
    pdfDoc.setFontSize(8);
    pdfDoc.setTextColor(110, 110, 110);
    pdfDoc.text(`Generated on: ${dateStr} ${timeStr}`, 40, pageHeight - 50);

    pdfDoc.text("Authorized Signature", pageWidth - 170, pageHeight - 50);
    pdfDoc.line(pageWidth - 170, pageHeight - 35, pageWidth - 40, pageHeight - 35);
  };

  // ---------------- Individual Client PDF ----------------
  const generateClientPDF = (clientData) => {
    const pdfDoc = new jsPDF({ unit: "pt", format: "a4" });

    addPdfHeader(pdfDoc);

    pdfDoc.setFont("helvetica", "bold");
    pdfDoc.setFontSize(13);
    pdfDoc.setTextColor(20, 30, 50);
    pdfDoc.text("Client Summary", 40, 115);

    autoTable(pdfDoc, {
      startY: 130,
      head: [["Client Information", ""]],
      body: [
        ["Client ID", clientData.clientId || "-"],
        ["Name", clientData.name || "-"],
        ["Phone", clientData.phone || "-"],
        ["Email", clientData.email || "-"],
        ["Address", clientData.address || "-"],
      ],
      theme: "grid",
      headStyles: { fillColor: [20, 30, 50], textColor: 255 },
      styles: { fontSize: 10, cellPadding: 6 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 150 } },
      margin: { left: 40, right: 40 },
    });

    autoTable(pdfDoc, {
      startY: pdfDoc.lastAutoTable.finalY + 20,
      head: [["Project Information", ""]],
      body: [
        ["Project Type", clientData.projectType || "-"],
        ["Status", clientData.status || "-"],
      ],
      theme: "grid",
      headStyles: { fillColor: [20, 30, 50], textColor: 255 },
      styles: { fontSize: 10, cellPadding: 6 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 150 } },
      margin: { left: 40, right: 40 },
    });

    addPdfFooter(pdfDoc);

    pdfDoc.save(`${clientData.clientId || "client"}-details.pdf`);
  };

  // ---------------- All Clients PDF ----------------
  const generateAllClientsPDF = () => {
    if (clients.length === 0) {
      alert("No clients to export.");
      return;
    }

    const pdfDoc = new jsPDF({ unit: "pt", format: "a4" });

    addPdfHeader(pdfDoc);

    pdfDoc.setFont("helvetica", "bold");
    pdfDoc.setFontSize(13);
    pdfDoc.setTextColor(20, 30, 50);
    pdfDoc.text("Client Report", 40, 115);

    pdfDoc.setFont("helvetica", "normal");
    pdfDoc.setFontSize(9);
    pdfDoc.setTextColor(90, 90, 90);
    const now = new Date();
    pdfDoc.text(`Generated: ${now.toLocaleDateString("en-IN")}`, 40, 130);
    pdfDoc.text(`Total Clients: ${clients.length}`, 40, 143);

    autoTable(pdfDoc, {
      startY: 160,
      head: [["Client ID", "Name", "Phone", "Email", "Project Type", "Status"]],
      body: clients.map((c) => [
        c.clientId || "-",
        c.name || "-",
        c.phone || "-",
        c.email || "-",
        c.projectType || "-",
        c.status || "-",
      ]),
      theme: "striped",
      headStyles: { fillColor: [20, 30, 50], textColor: 255, fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 5 },
      margin: { left: 40, right: 40 },
      didDrawPage: () => {
        addPdfFooter(pdfDoc);

        const pageWidth = pdfDoc.internal.pageSize.getWidth();
        const pageHeight = pdfDoc.internal.pageSize.getHeight();
        const pageCount = pdfDoc.internal.getNumberOfPages();
        const currentPage = pdfDoc.internal.getCurrentPageInfo().pageNumber;

        pdfDoc.setFontSize(8);
        pdfDoc.setTextColor(110, 110, 110);
        pdfDoc.text(
          `Page ${currentPage} of ${pageCount}`,
          pageWidth - 100,
          pageHeight - 50,
        );
      },
    });

    pdfDoc.save(`all-clients-report.pdf`);
  };

  // Shared action-icon cluster (View / Edit / Delete) used by every tab's
  // Actions column, so the In Talk tab and the other tabs stay identical.
  const renderActionIcons = (c) => (
    <div className="action-icons">
      <button
        className="icon-btn view-icon"
        title="View Client"
        onClick={() => viewClient(c)}
      >
        <FaEye />
      </button>
      <button
        className="icon-btn edit-icon"
        title="Edit Client"
        onClick={() => editClient(c)}
      >
        <FaEdit />
      </button>
      <button
        className="icon-btn delete-icon"
        title="Delete Client"
        onClick={() => deleteClient(c)}
      >
        <FaTrash />
      </button>
    </div>
  );

  const showProjectConfirmFields = !editingId && client.status === "In Progress";

  return (
    <div className="clients-page">
      <div className="clients-header">
        <div>
          <h1>Clients</h1>
          <p>Manage all construction clients.</p>
        </div>

        <button className="download-all-btn" onClick={generateAllClientsPDF}>
          <FaDownload />
          Download Client Details
        </button>
      </div>

      {/* ---------------- Client lifecycle tabs ---------------- */}
      <div className="client-tabs">
        {CLIENT_TABS.map((tab) => (
          <button
            key={tab}
            className={`client-tab ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
            {activeTab === tab && (
              <motion.div
                layoutId="client-tab-indicator"
                className="client-tab-indicator"
                transition={{ type: "spring", stiffness: 400, damping: 32 }}
              />
            )}
          </button>
        ))}
      </div>

      <div className="clients-toolbar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by ID, name, phone or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          className="add-client-btn"
          onClick={() => {
            resetClientForm();
            setShowModal(true);
          }}
        >
          <FaPlus />
          Add Client
        </button>
      </div>

      <div className="clients-table-card">
        <AnimatePresence mode="wait">
          <motion.table
            key={activeTab}
            className="clients-table"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <thead>
              {activeTab === "In Talk" ? (
                <tr>
                  <th>Client ID</th>
                  <th>Client Name</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Project Type</th>
                  <th>Status</th>
                  <th>Estimation</th>
                  <th>Actions</th>
                  <th>Projects</th>
                </tr>
              ) : activeTab === "On Hold" ? (
                <tr>
                  <th>Client ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Project Type</th>
                  <th>Status</th>
                  <th>Resume</th>
                  <th>Actions</th>
                  <th>Projects</th>
                </tr>
              ) : (
                <tr>
                  <th>Client ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Project Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                  <th>Projects</th>
                </tr>
              )}
            </thead>

            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td
                    colSpan={activeTab === "In Talk" || activeTab === "On Hold" ? 10 : 9}
                    className="empty-row"
                  >
                    No Clients Found
                  </td>
                </tr>
              ) : activeTab === "In Talk" ? (
                paginatedClients.map((c) => (
                  <tr key={c.id}>
                    <td>{c.clientId}{renderProjectRefBadge(c.id)}</td>
                    <td>{c.name}</td>
                    <td>{c.email}</td>
                    <td>{c.address}</td>
                    <td>{c.projectType || "-"}</td>
                    <td>
                      <span className="status-pill">{c.status}</span>
                    </td>
                    <td>
                      {estimationsMap[c.id] ? (
                        <span className="estimation-amount-pill">
                          {formatRupees(estimationsMap[c.id].amount)}
                        </span>
                      ) : (
                        <button
                          className="estimation-btn"
                          onClick={() => openEstimationModal(c)}
                        >
                          <FaCalculator />
                          Estimation
                        </button>
                      )}
                    </td>
                    <td>{renderActionIcons(c)}</td>
                    <td>
                      <button className="estimation-btn" onClick={() => openAddProject(c)}>
                        <FaPlus />
                        Add Project
                      </button>
                    </td>
                  </tr>
                ))
              ) : activeTab === "On Hold" ? (
                paginatedClients.map((c) => (
                  <tr key={c.id}>
                    <td>{c.clientId}{renderProjectRefBadge(c.id)}</td>
                    <td>{c.name}</td>
                    <td>{c.phone}</td>
                    <td>{c.email}</td>
                    <td>{c.address}</td>
                    <td>{c.projectType || "-"}</td>
                    <td>
                      <span className="status-pill status-onhold">{c.status}</span>
                    </td>
                    <td>
                      <button
                        className="estimation-btn"
                        disabled={resumingClientId === c.id}
                        onClick={() => resumeFromOnHold(c)}
                      >
                        <FaPlay />
                        {resumingClientId === c.id ? "Resuming..." : "Resume"}
                      </button>
                    </td>
                    <td>{renderActionIcons(c)}</td>
                    <td>
                      <button className="estimation-btn" onClick={() => openAddProject(c)}>
                        <FaPlus />
                        Add Project
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                paginatedClients.map((c) => (
                  <tr key={c.id}>
                    <td>{c.clientId}{renderProjectRefBadge(c.id)}</td>
                    <td>{c.name}</td>
                    <td>{c.phone}</td>
                    <td>{c.email}</td>
                    <td>{c.address}</td>
                    <td>{c.projectType || "-"}</td>
                    <td>
                      <span className="status-pill">{c.status}</span>
                    </td>
                    <td>{renderActionIcons(c)}</td>
                    <td>
                      <button className="estimation-btn" onClick={() => openAddProject(c)}>
                        <FaPlus />
                        Add Project
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </motion.table>
        </AnimatePresence>
      </div>

      <Pagination {...clientsPagination} />

      {/* Add / Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="client-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>{editingId ? "Edit Client" : "Add Client"}</h2>

              <div className="form-grid">
                {/* Client ID — manually entered, must start with "CL-" and
                    be unique. Read-only once the client exists (editing). */}
                <div className="form-field-wrap">
                  <input
                    type="text"
                    name="clientIdInput"
                    placeholder="Client ID (e.g. CL-001)"
                    value={clientIdInput}
                    disabled={!!editingId}
                    onChange={handleClientIdChange}
                  />
                  {!editingId && clientIdError && (
                    <span className="field-error">{clientIdError}</span>
                  )}
                </div>

                <input
                  type="text"
                  name="name"
                  placeholder="Client Name"
                  value={client.name}
                  onChange={handleChange}
                />

                <div className="form-field-wrap">
                  <input
                    type="text"
                    name="phone"
                    placeholder="Phone Number (10 digits)"
                    inputMode="numeric"
                    maxLength={10}
                    value={client.phone}
                    onChange={handleChange}
                  />
                  {formErrors.phone && (
                    <span className="field-error">{formErrors.phone}</span>
                  )}
                </div>

                <div className="form-field-wrap">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={client.email}
                    onChange={handleChange}
                  />
                  {formErrors.email && (
                    <span className="field-error">{formErrors.email}</span>
                  )}
                </div>

                <input
                  type="text"
                  name="address"
                  placeholder="Address"
                  value={client.address}
                  onChange={handleChange}
                />

                <select
                  name="projectType"
                  value={client.projectType}
                  onChange={handleChange}
                >
                  <option value="">Select Project Type</option>
                  <option value="Residential">Residential</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Renovation">Renovation</option>
                  <option value="Interiors">Interiors</option>
                </select>

                

                {/* Only shown when editing a client that already has an
                    estimation — lets the amount be corrected in place. */}
                {editingId && estimationEditDocId && (
                  <input
                    type="number"
                    name="estimationEditAmount"
                    placeholder="Estimation Amount"
                    value={estimationEditAmount}
                    onChange={(e) => setEstimationEditAmount(e.target.value)}
                  />
                )}

                {/* Direct Project Confirmation — only for a brand-new client
                    with status "In Progress". Confirming here creates the
                    client, estimation (pre-marked invoiced) and invoice in
                    one step; see confirmProjectDirectly(). */}
                {showProjectConfirmFields && (
                  <>
                    <div className="form-field-wrap">
                      <input
                        type="text"
                        name="finalQuotation"
                        inputMode="decimal"
                        placeholder="Final Quotation (₹)"
                        value={finalQuotation}
                        onChange={handleFinalQuotationChange}
                      />
                      {projectFormErrors.finalQuotation && (
                        <span className="field-error">
                          {projectFormErrors.finalQuotation}
                        </span>
                      )}
                    </div>

                    <div className="form-field-wrap">
                      <input
                        type="text"
                        name="advancePayment"
                        inputMode="decimal"
                        placeholder="Advance Payment Received (₹)"
                        value={advancePayment}
                        onChange={handleAdvancePaymentChange}
                      />
                      {projectFormErrors.advancePayment && (
                        <span className="field-error">
                          {projectFormErrors.advancePayment}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Project Budget Breakdown — only for a brand-new client
                  being confirmed straight into "In Progress". Becomes the
                  project's ORIGINAL PLAN once Confirm Project is clicked;
                  see confirmProjectDirectly() and the budgetBreakdown /
                  budgetPlans writes there. */}
              {showProjectConfirmFields && (
                <div className="budget-breakdown-section">
                  <h3 className="budget-breakdown-title">Project Budget Breakdown</h3>
                  <p className="budget-breakdown-hint">
                    Split the final quotation into categories (Material, Labour,
                    Electrical, Painting, etc). This becomes the project's original
                    budget plan and will be used later by Materials.
                  </p>

                  <div className="budget-breakdown-table">
                    <div className="budget-row budget-row-head">
                      <span>Category</span>
                      <span>Description</span>
                      <span>Planned Amount (₹)</span>
                      <span aria-hidden="true"></span>
                    </div>

                    <div className="budget-rows-scroll">
                      {budgetRows.map((row) => (
                        <div className="budget-row" key={row.id}>
                          <input
                            type="text"
                            list="budget-category-options"
                            placeholder="e.g. Material"
                            value={row.category}
                            onChange={(e) =>
                              updateBudgetRow(row.id, "category", e.target.value)
                            }
                          />
                          <input
                            type="text"
                            placeholder="e.g. Cement"
                            value={row.description}
                            onChange={(e) =>
                              updateBudgetRow(row.id, "description", e.target.value)
                            }
                          />
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="Amount"
                            value={row.amount}
                            onChange={(e) =>
                              updateBudgetRow(row.id, "amount", e.target.value)
                            }
                          />
                          <button
                            type="button"
                            className="budget-row-remove"
                            title="Remove Row"
                            disabled={budgetRows.length === 1}
                            onClick={() => removeBudgetRow(row.id)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <datalist id="budget-category-options">
                    <option value="Material" />
                    <option value="Labour" />
                    <option value="Electrical" />
                    <option value="Plumbing" />
                    <option value="Painting" />
                  </datalist>

                  <div className="budget-breakdown-footer">
                    <button
                      type="button"
                      className="budget-add-row-btn"
                      onClick={addBudgetRow}
                    >
                      <FaPlus />
                      Add Row
                    </button>

                    <div
                      className={`budget-total-pill${
                        Math.abs(totalPlannedAmount - Number(finalQuotation || 0)) > 0.01
                          ? " budget-total-mismatch"
                          : ""
                      }`}
                    >
                      Total Planned: <strong>{formatRupees(totalPlannedAmount)}</strong>
                    </div>
                  </div>

                  {budgetError && (
                    <span className="field-error budget-breakdown-error">
                      {budgetError}
                    </span>
                  )}
                </div>
              )}

              <div className="modal-buttons">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    resetClientForm();
                    setShowModal(false);
                  }}
                >
                  Cancel
                </button>

                <button
                  className="save-btn"
                  disabled={confirmingProject}
                  onClick={saveClient}
                >
                  {editingId
                    ? "Update Client"
                    : showProjectConfirmFields
                    ? confirmingProject
                      ? "Confirming..."
                      : "Confirm Project"
                    : "Save Client"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Client Modal */}
      <AnimatePresence>
        {showViewModal && viewingClient && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="client-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Client Details</h2>

              <div className="view-grid">
                <div className="view-field">
                  <span className="view-label">Client ID</span>
                  <span className="view-value">
                    {viewingClient.clientId || "-"}
                    {renderProjectRefBadge(viewingClient.id)}
                  </span>
                </div>
                <div className="view-field">
                  <span className="view-label">Name</span>
                  <span className="view-value">{viewingClient.name || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Phone</span>
                  <span className="view-value">{viewingClient.phone || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Email</span>
                  <span className="view-value">{viewingClient.email || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Address</span>
                  <span className="view-value">{viewingClient.address || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Project Type</span>
                  <span className="view-value">{viewingClient.projectType || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Status</span>
                  <span className="view-value">{viewingClient.status || "-"}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Estimation Amount</span>
                  <span className="view-value">
                    {estimationsMap[viewingClient.id]
                      ? formatRupees(estimationsMap[viewingClient.id].amount)
                      : "Not Created"}
                  </span>
                </div>
                <div className="view-field">
                  <span className="view-label">Created Date</span>
                  <span className="view-value">
                    {formatDate(viewingClient.createdAt)}
                  </span>
                </div>
              </div>

              <div className="modal-buttons">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingClient(null);
                  }}
                >
                  Close
                </button>

                <button
                  className="save-btn pdf-btn"
                  onClick={() => generateClientPDF(viewingClient)}
                >
                  <FaFilePdf />
                  Generate PDF
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Estimation Modal */}
      <AnimatePresence>
        {showEstimationModal && estimationClient && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="estimation-modal"
              initial={{ opacity: 0, scale: 0.9, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <button className="estimation-close" onClick={closeEstimationModal}>
                <FaTimes />
              </button>

              <div className="estimation-modal-header">
                <span className="estimation-eyebrow">Create Estimation</span>
                <h2>{estimationClient.name}</h2>
                <p>{estimationClient.clientId}</p>
              </div>

              <div className="estimation-buttons">
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  className="estimation-action move-estimation"
                  disabled={savingEstimation}
                  onClick={moveToEstimation}
                >
                  {savingEstimation ? "Saving..." : "Move to Estimation"}
                </motion.button>

                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  className="estimation-action move-invoice"
                  onClick={moveToInvoice}
                >
                  Move to Invoice
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Move to Invoice — Confirmation Modal */}
      {/* Move to Invoice — Confirmation Modal */}
      <AnimatePresence>
        {showMoveToInvoiceModal && moveToInvoiceClient && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="move-to-invoice-modal"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {/* ---- Sticky header ---- */}
              <div className="move-invoice-header">
                <div className="move-invoice-header-titles">
                  <span className="move-invoice-eyebrow">Project Planning &amp; Budget Allocation</span>
                  <h2>Move to Invoice</h2>
                </div>
              </div>

              {/* ---- Body: client info (fixed) + budget breakdown (scrolls) ---- */}
              <div className="move-invoice-body">
                {/* Section 1 — Client Information (shown first) */}
                <div className="move-invoice-client-section">
                  <h3 className="move-invoice-section-title">Client Information</h3>
                  <div className="move-invoice-client-cards">
                    <div className="mi-card">
                      <span className="mi-card-label">Client Name</span>
                      <span className="mi-card-value">{moveToInvoiceClient.name || "-"}</span>
                    </div>
                    <div className="mi-card">
                      <span className="mi-card-label">Client ID</span>
                      <span className="mi-card-value">{moveToInvoiceClient.clientId || "-"}</span>
                    </div>
                    <div className="mi-card">
                      <span className="mi-card-label">Project Type</span>
                      <span className="mi-card-value">{moveToInvoiceClient.projectType || "-"}</span>
                    </div>
                    <div className="mi-card">
                      <span className="mi-card-label">Current Status</span>
                      <span className="mi-card-value">{moveToInvoiceClient.status || "-"}</span>
                    </div>
                  </div>
                </div>

                {/* Section 2 — Project Budget Breakdown (shown after Client Information) */}
                <div className="move-invoice-budget-section">
                  <div className="move-invoice-budget-header">
                    <h3 className="move-invoice-section-title">Project Budget Breakdown</h3>
                    <p className="budget-breakdown-hint">
                      Split the final estimation into materials/line items. This becomes
                      the project's original budget plan and will be used later by Materials.
                    </p>
                  </div>

                  <div className="move-budget-table-wrap">
                    <div className="move-budget-row move-budget-row-head">
                      <span>Material / Work</span>
                      <span>Quantity</span>
                      <span>Unit Price (₹)</span>
                      <span>Total (₹)</span>
                      <span>Supplier</span>
                      <span>Notes</span>
                      <span aria-hidden="true"></span>
                    </div>

                    <div className="move-budget-rows-scroll">
                      {moveBudgetRows.map((row) => {
                        const rowTotal =
                          (Number(row.quantity) || 0) * (Number(row.unitPrice) || 0);
                        return (
                          <div className="move-budget-row" key={row.id}>
                            <input
                              type="text"
                              placeholder="e.g. Cement (OPC 53 Grade)"
                              value={row.name}
                              onChange={(e) =>
                                updateMoveBudgetRow(row.id, "name", e.target.value)
                              }
                            />
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="Qty"
                              value={row.quantity}
                              onChange={(e) =>
                                updateMoveBudgetRow(row.id, "quantity", e.target.value)
                              }
                            />
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="Unit Price"
                              value={row.unitPrice}
                              onChange={(e) =>
                                updateMoveBudgetRow(row.id, "unitPrice", e.target.value)
                              }
                            />
                            <span className="move-budget-row-total">
                              {rowTotal > 0 ? formatRupees(rowTotal) : "-"}
                            </span>
                            <input
                              type="text"
                              placeholder="Supplier"
                              value={row.supplier}
                              onChange={(e) =>
                                updateMoveBudgetRow(row.id, "supplier", e.target.value)
                              }
                            />
                            <input
                              type="text"
                              placeholder="Notes"
                              value={row.notes}
                              onChange={(e) =>
                                updateMoveBudgetRow(row.id, "notes", e.target.value)
                              }
                            />
                            <button
                              type="button"
                              className="budget-row-remove"
                              title="Remove Row"
                              disabled={moveBudgetRows.length === 1}
                              onClick={() => removeMoveBudgetRow(row.id)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="move-budget-add-row">
                    <button type="button" className="budget-add-row-btn" onClick={addMoveBudgetRow}>
                      <FaPlus />
                      Add Row
                    </button>
                  </div>

                  {moveBudgetError && (
                    <span className="field-error budget-breakdown-error">{moveBudgetError}</span>
                  )}
                </div>

                {/* Section 3 — Final Quotation + Advance Payment (shown after Budget Breakdown) */}
                <div className="move-invoice-client-section">
                  <h3 className="move-invoice-section-title">Final Quotation</h3>
                  <div className="move-invoice-client-cards">
                    <div className="mi-card mi-card-input">
                      <span className="mi-card-label">Final Estimation (₹)</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Enter final estimation amount"
                        value={moveFinalEstimationInput}
                        onChange={handleMoveFinalEstimationChange}
                        autoFocus
                      />
                      {moveFinalEstimationError && (
                        <span className="field-error">{moveFinalEstimationError}</span>
                      )}
                    </div>
                    <div className="mi-card mi-card-highlight">
                      <span className="mi-card-label">Current Advance</span>
                      <span className="mi-card-value">
                        {formatRupees(Number(moveAdvancePayment) || 0)}
                      </span>
                    </div>
                    <div className="mi-card mi-card-input">
                      <span className="mi-card-label">Advance Payment Received (₹)</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Enter amount received"
                        value={moveAdvancePayment}
                        onChange={handleMoveAdvanceChange}
                      />
                      {moveAdvanceError && (
                        <span className="field-error">{moveAdvanceError}</span>
                      )}
                    </div>
                  </div>

                  {/* Sticky summary — stays visible while rows scroll */}
                  <div className="move-invoice-summary-bar" style={{ marginTop: 16 }}>
                    <div className="mi-summary-chip">
                      <span className="mi-summary-label">Estimation Grand Total</span>
                      <strong>{formatRupees(moveTotalPlannedAmount)}</strong>
                    </div>
                    <div className="mi-summary-chip mi-summary-ok">
                      <span className="mi-summary-label">Final Quotation</span>
                      <strong>{formatRupees(moveFinalEstimation)}</strong>
                    </div>
                  </div>
                </div>

                {/* Section 4 — Is this the final quotation? (shown last) */}
                <div className="mi-card" style={{ marginTop: 16 }}>
                  <span className="mi-card-label">Is this the final quotation?</span>
                  <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                    <button
                      type="button"
                      className={`estimation-btn${moveQuotationConfirmed === true ? " is-active" : ""}`}
                      onClick={() => setMoveQuotationConfirmed(true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`estimation-btn${moveQuotationConfirmed === false ? " is-active" : ""}`}
                      onClick={() => setMoveQuotationConfirmed(false)}
                    >
                      No
                    </button>
                  </div>
                  {moveQuotationConfirmed === false && (
                    <span className="field-error" style={{ marginTop: 6, display: "block" }}>
                      Please adjust the Final Quotation above, then confirm "Yes" once it's final.
                    </span>
                  )}
                </div>
              </div>

              {/* ---- Sticky footer ---- */}
              <div className="move-invoice-footer">
                <button
                  className="cancel-btn"
                  disabled={movingToInvoice}
                  onClick={closeMoveToInvoiceModal}
                >
                  Cancel
                </button>

                <button
                  className="save-btn"
                  disabled={movingToInvoice || moveQuotationConfirmed !== true}
                  onClick={confirmMoveToInvoice}
                >
                  {movingToInvoice ? "Confirming..." : "Continue & Move to Invoice"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Project Modal — creates an additional project for an
          EXISTING client. Client ID and Name are read-only (never
          duplicated); Phone/Email/Address/Project Type are editable per
          project, since a new project may have a different site, contact,
          or project type. Saving creates a new `clients` document that
          reuses the same Client ID, so it enters the exact same, untouched
          In Talk -> Estimation -> Invoice -> Payments -> Materials
          pipeline as any other client. */}
      <AnimatePresence>
        {showAddProjectModal && addProjectSource && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="client-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Add Project — {addProjectSource.name}</h2>
              <p className="reject-choice-message">
                This creates a new project for the same client. The Client ID and
                name stay the same; the new project starts fresh at "In Talk" and
                goes through Estimation and Invoice independently of this client's
                other projects.
              </p>

              <div className="form-grid">
                <input
                  type="text"
                  value={addProjectSource.clientId || ""}
                  disabled
                  title="Client ID stays the same across all of this client's projects"
                />

                <input
                  type="text"
                  value={addProjectSource.name || ""}
                  disabled
                  title="Client name stays the same across all of this client's projects"
                />

                <div className="form-field-wrap">
                  <input
                    type="text"
                    name="phone"
                    placeholder="Phone Number (10 digits)"
                    inputMode="numeric"
                    maxLength={10}
                    value={addProjectForm.phone}
                    onChange={handleAddProjectChange}
                  />
                  {addProjectErrors.phone && (
                    <span className="field-error">{addProjectErrors.phone}</span>
                  )}
                </div>

                <div className="form-field-wrap">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={addProjectForm.email}
                    onChange={handleAddProjectChange}
                  />
                  {addProjectErrors.email && (
                    <span className="field-error">{addProjectErrors.email}</span>
                  )}
                </div>

                <input
                  type="text"
                  name="address"
                  placeholder="Address for this project"
                  value={addProjectForm.address}
                  onChange={handleAddProjectChange}
                />

                <div className="form-field-wrap">
                  <select
                    name="projectType"
                    value={addProjectForm.projectType}
                    onChange={handleAddProjectChange}
                  >
                    <option value="">Select Project Type</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Renovation">Renovation</option>
                    <option value="Interiors">Interiors</option>
                  </select>
                  {addProjectErrors.projectType && (
                    <span className="field-error">{addProjectErrors.projectType}</span>
                  )}
                </div>
              </div>

              <div className="modal-buttons">
                <button className="cancel-btn" onClick={closeAddProject}>
                  Cancel
                </button>

                <button
                  className="save-btn"
                  disabled={addProjectSaving}
                  onClick={saveAddProject}
                >
                  {addProjectSaving ? "Creating..." : "Create Project"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

       {/* ---- 5th AnimatePresence: Delete / Reject choice modal ---- */}
      {/* ONLY for clients currently "In Talk". Reuses the same
          modal-overlay/client-modal styling as every other modal on this
          page instead of introducing a new visual style. */}
      <AnimatePresence>
        {pendingDeleteClient && pendingDeleteClient.status === "In Talk" && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="client-modal reject-choice-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Remove this client?</h2>
              <p className="reject-choice-message">
                "{pendingDeleteClient.name}" is still In Talk. You can move them to
                Rejected — keeping their record — or permanently delete them instead.
              </p>

              <div className="modal-buttons reject-choice-buttons">
                <button
                  className="cancel-btn"
                  disabled={deletingClient || movingToRejected}
                  onClick={() => setPendingDeleteClient(null)}
                >
                  Cancel
                </button>

                <button
                  className="save-btn reject-btn"
                  disabled={deletingClient || movingToRejected}
                  onClick={confirmMoveToRejected}
                >
                  {movingToRejected ? "Moving..." : "Move to Rejected"}
                </button>

                <button
                  className="save-btn danger-btn"
                  disabled={deletingClient || movingToRejected}
                  onClick={confirmDeleteClient}
                >
                  {deletingClient ? "Deleting..." : "Permanently Delete"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

       <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />

      {/* Plain permanent-delete confirmation — everything except "In Talk"
          (In Progress / Completed / Rejected) keeps the original behaviour.
          When pendingDeleteClient IS "In Talk", the AnimatePresence block
          above handles it instead, so this stays closed. */}
      <ConfirmDialog
        open={!!pendingDeleteClient && pendingDeleteClient.status !== "In Talk"}
        title="Delete this client?"
        message="This will also remove their linked estimation, if any. This can't be undone."
        variant="danger"
        loading={deletingClient}
        confirmLabel="Delete"
        onConfirm={confirmDeleteClient}
        onCancel={() => !deletingClient && setPendingDeleteClient(null)}
      />
    </div>
  );
}

export default Clients;